import React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Users, Shield, Ban, Building2, Banknote, Calendar, BookOpen, Trash2, Inbox, HelpCircle, MessageSquare } from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Loading } from "@/components/common/Loading";
import { StatCard } from "@/components/dashboard/StatCard";
import { AdminRefreshButton } from "@/components/admin/AdminRefreshButton";
import { DigestDefaultMigrationCard } from "@/components/admin/DigestDefaultMigrationCard";

const TIER_LABELS = { standard: "Standard", pro: "Pro", premium: "Premium", business: "Business" };

export default function AdminOverview() {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["admin-overview"],
    queryFn: async () => (await api.get("/admin/overview")).data,
  });
  if (isLoading || !data) return <Loading />;
  const { users, content, engagement } = data;

  return (
    <div data-testid="admin-overview">
      <div className="mb-4 flex items-center justify-end">
        <AdminRefreshButton
          testId="admin-refresh-overview"
          onRefresh={() => queryClient.invalidateQueries({ queryKey: ["admin-overview"] })}
        />
      </div>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard icon={Users} label="Total members" value={users.total} testId="admin-stat-users" />
        <StatCard icon={Shield} label="Admins" value={users.admins} testId="admin-stat-admins" />
        <StatCard icon={Ban} label="Suspended" value={users.suspended} testId="admin-stat-suspended" />
        <StatCard icon={Trash2} label="Soft-deleted content" value={content.deleted} sub="Recoverable" testId="admin-stat-deleted" />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card className="border-0 p-6">
          <h2 className="text-lg font-[640] text-foreground">Members by tier</h2>
          <div className="mt-4 space-y-3">
            {["standard", "pro", "premium", "business"].map((k) => {
              const v = users.by_tier[k] || 0;
              const pct = users.total ? Math.round((v / users.total) * 100) : 0;
              return (
                <div key={k} data-testid={`admin-tier-${k}`}>
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-[540] text-foreground">{TIER_LABELS[k]}</span>
                    <span className="text-muted-foreground">{v} · {pct}%</span>
                  </div>
                  <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-secondary">
                    <div className="h-full rounded-full bg-primary" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </Card>

        <Card className="border-0 p-6">
          <h2 className="text-lg font-[640] text-foreground">Published content</h2>
          <div className="mt-3 grid grid-cols-2 gap-3">
            {[
              { icon: Building2, label: "Businesses", v: content.businesses },
              { icon: Banknote, label: "Capital", v: content.capital },
              { icon: Calendar, label: "Events", v: content.events },
              { icon: BookOpen, label: "Resources", v: content.resources },
            ].map((x) => (
              <div key={x.label} className="flex items-center gap-3 rounded-lg bg-secondary/50 px-3 py-2.5">
                <x.icon className="h-4 w-4 text-primary" />
                <div><div className="text-lg font-[680] text-foreground">{x.v}</div><div className="text-xs text-muted-foreground">{x.label}</div></div>
              </div>
            ))}
          </div>
          <h2 className="mt-6 text-lg font-[640] text-foreground">Engagement</h2>
          <div className="mt-3 divide-y divide-border/70">
            <div className="flex items-center justify-between py-2.5 text-sm"><span className="flex items-center gap-2 text-foreground/80"><Inbox className="h-4 w-4 text-muted-foreground" /> Pending inquiries</span><span className="font-[640]">{engagement.pending_inquiries}</span></div>
            <div className="flex items-center justify-between py-2.5 text-sm"><span className="flex items-center gap-2 text-foreground/80"><HelpCircle className="h-4 w-4 text-muted-foreground" /> Open questions</span><span className="font-[640]">{engagement.open_questions}</span></div>
            <div className="flex items-center justify-between py-2.5 text-sm"><span className="flex items-center gap-2 text-foreground/80"><MessageSquare className="h-4 w-4 text-muted-foreground" /> Chatter posts</span><span className="font-[640]">{engagement.chatter_posts}</span></div>
          </div>
        </Card>
      </div>

      <div className="mt-6">
        <DigestDefaultMigrationCard />
      </div>
    </div>
  );
}
